import { Box } from '@mui/material';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useViewMode } from 'apps/wrapper/src/hooks/use-view-mode';
import { PageRoutes } from 'apps/wrapper/src/routing/page-routes';
import { IContactProfile } from 'apps/wrapper/src/services/api-client/interface';
import moment from 'moment';
import { FC, useCallback, useMemo, useState } from 'react';
import { ContactActions } from './contact-actions/contact-actions';
import { LocationSwitchDialog } from './dialogs/location-switch/location-switch-dialog';
import { NetworkSwitchDialog } from './dialogs/network-switch/network-switch-dialog';
import { ContactInfoList, ContactName, SearchResultsItemWrapper } from './search-results.styles';
import { useHistory } from 'react-router-dom';

export interface SearchResultsItemProps {
  contact: IContactProfile;
  onClose: () => void;
}

export const SearchResultsItem: FC<SearchResultsItemProps> = (props) => {
  const { contact, onClose } = props;
  const history = useHistory();
  const { viewMode } = useViewMode();
  const { locations, selectedLocation, switchLocation } = useLocations();
  const [locationSwitchDialogOpen, setLocationSwitchDialogOpen] = useState(false);
  const [networkSwitchDialogOpen, setNetworkSwitchDialogOpen] = useState(false);
  const isPhone = viewMode === 'Phone';

  const formatPhone = (phoneNumber: string | null) =>
    phoneNumber && phoneNumber.length > 0
      ? `(${phoneNumber.substring(0, 3)})${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6)}`
      : '';

  const getContactPhone = (contact: IContactProfile) => {
    if (contact.cellPhone) return contact.cellPhone;
    if (contact.workPhone) return contact.workPhone;
    if (contact.homePhone) return contact.homePhone;
    return null;
  };

  const getLocationName = useMemo(() => {
    return locations.find((x) => x.id === contact.location.id)?.name;
  }, [contact, locations]);

  const handleContactNameClick = useCallback(() => {
    if (contact.location.id === selectedLocation?.id) {
      goToFullPatientProfile(contact.id);
      return;
    }

    const contactNetwork = locations.find((x) => x.id === contact.location.id)?.idNetwork;
    const selectedLocationNetwork = locations.find((x) => x.id === selectedLocation?.id)?.idNetwork;

    if (contactNetwork === selectedLocationNetwork) {
      setLocationSwitchDialogOpen(true);
    } else {
      setNetworkSwitchDialogOpen(true);
    }
  }, [contact, selectedLocation]);

  const goToFullPatientProfile = (patientId: number, locationId?: number) => {
    const goToRoute = `/${PageRoutes.PATIENT_PROFILE}/${patientId}`;

    const queryParams = new URLSearchParams();

    if (locationId) {
      queryParams.set('locationId', locationId.toString());
    }

    queryParams.set('backTo', history.location.pathname + (history.location.search || ''));

    if (history.location.pathname === goToRoute) {
      onClose && onClose();
      return;
    }

    // check if the router sttars with patient profile if yes then replace the route
    if (history.location.pathname.startsWith(`/${PageRoutes.PATIENT_PROFILE}`)) {
      history.replace({
        pathname: goToRoute,
        search: queryParams.toString()
      });
    } else {
      history.push({
        pathname: goToRoute,
        search: queryParams.toString()
      });
    }

    onClose && onClose();
  };

  const handleContactInCurrentLocation = () => {
    goToFullPatientProfile(contact.id);
    onClose && onClose();
  };

  const handleContactInAnotherLocation = async () => {
    await switchLocation(contact.location.id);
    goToFullPatientProfile(contact.id, contact.location.id);
  };

  return (
    <>
      <SearchResultsItemWrapper>
        <Box>
          <ContactName onClick={handleContactNameClick}>
            {contact.firstName} {contact.lastName}
          </ContactName>
          <ContactInfoList isPhone={isPhone}>
            {contact.dateOfBirth && <li>{moment(contact.dateOfBirth).format('MM/DD/YYYY')}</li>}
            {getContactPhone(contact) && <li>{formatPhone(getContactPhone(contact))}</li>}
            <li>{getLocationName}</li>
          </ContactInfoList>
        </Box>
        <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
          <ContactActions contact={contact} onClose={onClose} />
        </Box>
      </SearchResultsItemWrapper>

      <LocationSwitchDialog
        open={locationSwitchDialogOpen}
        patientName={`${contact.firstName} ${contact.lastName}`}
        locationName={locations.find((x) => x.id === contact.location.id)?.name}
        onClose={() => setLocationSwitchDialogOpen(false)}
        onUseCurrentLocation={handleContactInCurrentLocation}
        onSwitchLocation={handleContactInAnotherLocation}
      />

      <NetworkSwitchDialog
        open={networkSwitchDialogOpen}
        patientName={`${contact.firstName} ${contact.lastName}`}
        locationName={locations.find((x) => x.id === contact.location.id)?.name}
        onClose={() => setNetworkSwitchDialogOpen(false)}
        onSwitchLocation={handleContactInAnotherLocation}
      />
    </>
  );
};
